import { useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import Select from 'modules/core/components/Inputs/Select'
import TextField from 'modules/core/components/TextField'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { useFormProvider } from 'modules/processes/components/ItemWizard/Providers/form'
import { useValidationsProvider } from 'modules/processes/components/ItemWizard/Providers/validations'
import CollapsableSection from 'modules/processes/components/ItemWizard/components/CollapsableSection'
import type ItemForm from 'modules/processes/components/ItemWizard/components/Form/ItemForm'
import { defaultOffsetDirection } from 'modules/processes/components/ItemWizard/components/Form/Items/Deadline/utils'
import { StepDirections } from 'modules/processes/components/ProcessSettings/Steps/StepDescription'
import { StepProgressionCondtion } from 'modules/processes/components/ProcessSidePanel'
import { Template } from 'modules/processes/domain'
import { DueDatesFields } from 'modules/processes/hooks/useProcesses'
import { MaterializedItemsFormValues } from 'modules/processes/hooks/useWizardItemForm'
import { useProcessContext } from 'modules/processes/pages/Process/ProcessRootPage'

const offsetDirection = (selectedReference: DueDatesFields | null | undefined) => {
  if (selectedReference && inlcudesStartDate(selectedReference))
    return [...defaultOffsetDirection()].slice(0, 1)

  return defaultOffsetDirection()
}

const inlcudesStartDate = (selectedReference: DueDatesFields) =>
  [DueDatesFields.StepStartDate, DueDatesFields.ProcessStartDate].includes(selectedReference)

// Available fields come for now from the template
export const optionsForReferenceFields = (
  currentOption: string | null,
  orderedFields: string[],
  template: Template | undefined
) => {
  if (typeof template == 'undefined' || orderedFields.length == 0) return []

  const inputs = template.processInputFields.filter((input) => {
    return (
      input.data_type == 'date' && (orderedFields.includes(input.key) || input.key == currentOption)
    )
  })

  const sortedInputs = inputs?.sort((a, b) => {
    const indexA = orderedFields.indexOf(a.key)
    const indexB = orderedFields.indexOf(b.key)
    return indexA - indexB
  })

  return sortedInputs.map((input) => ({ value: input.key, label: input.label }))
}

type ChangeValueType = (
  key: keyof MaterializedItemsFormValues,
  value: number | string | undefined | number[]
) => void

interface Props {
  readonly: boolean
  form: ItemForm
  onEnabledChange: () => void
  openedForm: boolean
  template: Template | undefined
  changeValue: ChangeValueType
}

const DueDateForm = ({
  readonly,
  form,
  onEnabledChange,
  openedForm,
  template,
  changeValue,
}: Props) => {
  const [offsetDirectionOptions, setOffsetDirectionOptions] = useState(
    offsetDirection(form.get('reference_field_key').value)
  )

  const { taskConfigurationSettings } = useProcessContext()
  const { currentStep } = useFormProvider()
  const { setFieldValidation, removeFieldValidation } = useValidationsProvider()
  const referenceOptions = optionsForReferenceFields(
    form.get('reference_field_key').value,
    taskConfigurationSettings?.referenceFieldKeys || [],
    template
  )
  const { offsetValue, offsetError, onChange } = useOffsetValue(form, changeValue)
  const invalidDueDateByDate = (() => {
    const {
      offsetDirection: stepOffsetDirection,
      offsetAmount: stepOffsetAmount,
      progressionCondition,
      progressionConditionResourceKey,
    } = currentStep

    const {
      offset_amount: formOffsetAmount,
      offset_direction: formOffsetDirection,
      reference_field_key: resource,
    } = form.values

    const byDateStep = progressionCondition === StepProgressionCondtion.ByDate
    const sameOffsetDirection = stepOffsetDirection === formOffsetDirection
    const sameProgressionCondition = progressionConditionResourceKey === resource
    const stepDirectionAfter = stepOffsetDirection === StepDirections.after
    const formDirectionBefore = formOffsetDirection === StepDirections.before

    if (!byDateStep) return false

    if (sameOffsetDirection && sameProgressionCondition) {
      return stepDirectionAfter
        ? Number(stepOffsetAmount) > formOffsetAmount
        : Number(stepOffsetAmount) < formOffsetAmount
    } else {
      if (stepDirectionAfter && formDirectionBefore) {
        return formOffsetAmount > 0
      } else {
        return false
      }
    }
  })()

  const setOffsetDirection = () => {
    const direction = offsetDirection(form.get('reference_field_key').value)

    setOffsetDirectionOptions(direction)

    const directionValues = direction.map((d) => d.value)

    if (!directionValues.includes(form.get('offset_direction').value)) {
      changeValue('offset_direction', direction[0]?.value)
    }
  }

  useUnsafeEffect(() => {
    const field = 'due_date'

    setFieldValidation({
      field,
      validation: () => !openedForm || !invalidDueDateByDate,
    })

    return () => {
      removeFieldValidation(field)
    }
  }, [invalidDueDateByDate, openedForm])

  if (referenceOptions?.length == 0) return null

  return (
    <>
      <Box gap="s6">
        <CollapsableSection
          disabled={readonly}
          defaultOpen={openedForm}
          cbk={onEnabledChange}
          title={i18n.t('processes.process_settings.wizard.deadline.label')}
          description={i18n.t('processes.process_settings.tasks.form.deadline_desc')}
          contentPadding="s0"
        >
          <Box gap="s12" paddingX="s16" paddingBottom="s24">
            <Box gap="s6" flexDirection="row" alignItems="center">
              <Box width="full" flexDirection="row" gap="s8" alignItems="flexEnd">
                <TextField
                  mandatory
                  type="number"
                  min={0}
                  error={offsetError ? ' ' : undefined}
                  readonly={readonly}
                  value={offsetValue}
                  onChange={onChange}
                  indicator={i18n.t('common.day.other')}
                />
              </Box>
              <Box width="full">
                <Select
                  readonly={readonly}
                  field={form.get('offset_direction')}
                  options={offsetDirectionOptions}
                  onChange={() => {
                    setOffsetDirection()
                  }}
                />
              </Box>
            </Box>
            <Select
              readonly={readonly}
              field={form.get('reference_field_key')}
              options={referenceOptions}
              onChange={() => {
                setOffsetDirection()
              }}
            />
            {offsetError && (
              <Text.Normal size="200" color="radicalRed1000">
                {offsetError}
              </Text.Normal>
            )}
            {invalidDueDateByDate && (
              <Box
                padding="s24"
                background="tangerine200"
                borderRadius={{ all: 'abs016' }}
                gap="s8"
              >
                <Box flexDirection="row" alignItems="center" gap="s4">
                  <Icon.Medium icon="alert-sign" color="tangerine1000" />
                  <Text.Normal size="200" weight="semibold" color="grey1000">
                    {i18n.t('processes.process_settings.tasks.form.deadline_warning.title')}
                  </Text.Normal>
                </Box>

                <Text.Normal size="200" color="grey1000">
                  {i18n.t('processes.process_settings.tasks.form.deadline_warning.description')}
                </Text.Normal>
              </Box>
            )}
          </Box>
        </CollapsableSection>
      </Box>
    </>
  )
}
const useOffsetValue = (form: ItemForm, changeValue: ChangeValueType) => {
  const offsetValue = form.get('offset_amount').value
  const offsetError =
    offsetValue < 0
      ? i18n.t('processes.process_settings.tasks.form.validation.positive_value')
      : undefined

  const onChange = (value: string) => changeValue('offset_amount', +value)

  return {
    offsetValue,
    offsetError,
    onChange,
  }
}
export default DueDateForm
